/**
 * Guide Chat Modal
 *
 * Chat interface styled to the selected guide's personality.
 * Follows Pemberton pattern from pocket-butler.
 *
 * Australian English throughout.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Loader2, AlertCircle, MessageCircle, Sparkles, ArrowRight } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { useGuide } from '../../context/GuideContext';
import { usePrivacyAuth } from '../../context/PrivacyAuthContext';
import { useApp } from '../../context/AppContext';
import GuideSupportService from '../../services/GuideSupportService';
import { getReadingModule, moduleUserContext } from '../../utils/companionContext';
import { ConsentGate } from '../coaching/CoachingPanels';

/**
 * Message Bubble Component
 */
const MessageBubble = ({ message, isUser, guide }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className={`flex ${isUser ? 'justify-end' : 'justify-start'} ${!isUser ? 'items-end gap-2' : ''}`}
  >
    {!isUser && (
      <div
        className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-white text-sm font-bold"
        style={{
          backgroundColor: guide.accentColor,
          border: `2px solid ${guide.accentColor}`
        }}
      >
        {guide.name[0]}
      </div>
    )}
    <div
      className={`max-w-[80%] rounded-2xl px-4 py-3 ${
        isUser
          ? 'bg-purple-600 text-white rounded-br-md'
          : 'bg-slate-700 text-slate-100 rounded-bl-md'
      }`}
    >
      {isUser ? (
        <p className="text-sm whitespace-pre-wrap">{message.content}</p>
      ) : (
        <div className="text-sm prose prose-sm prose-invert max-w-none prose-p:my-1 prose-ul:my-1 prose-li:my-0.5">
          <ReactMarkdown>{message.content}</ReactMarkdown>
        </div>
      )}
    </div>
  </motion.div>
);

/**
 * Typing Indicator
 */
const TypingIndicator = ({ guide }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    className="flex justify-start items-end gap-2"
  >
    <motion.div
      className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold"
      style={{ backgroundColor: guide.accentColor }}
      animate={{ rotate: [-2, 2, -2] }}
      transition={{ duration: 1.5, repeat: Infinity }}
    >
      {guide.name[0]}
    </motion.div>
    <div className="bg-slate-700 rounded-2xl rounded-bl-md px-4 py-3">
      <p className="text-xs text-slate-400 mb-1">{guide.thinkingText}</p>
      <div className="flex gap-1">
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: guide.accentColor }}
            animate={{ y: [0, -4, 0] }}
            transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.15 }}
          />
        ))}
      </div>
    </div>
  </motion.div>
);

/**
 * Suggested Questions
 */
const SuggestedQuestions = ({ questions, onSelect, disabled, guide }) => (
  <div className="px-4 pb-3">
    <p className="text-xs text-slate-400 mb-2">You might ask:</p>
    <div className="flex flex-wrap gap-2">
      {questions.map((q, idx) => (
        <button
          key={idx}
          onClick={() => onSelect(q)}
          disabled={disabled}
          className="px-3 py-1.5 bg-slate-800 text-slate-300 rounded-full text-xs hover:bg-slate-700 transition-colors disabled:opacity-50 border border-slate-700"
        >
          {q}
        </button>
      ))}
    </div>
  </div>
);

/**
 * Sign In Prompt
 */
const SignInPrompt = ({ guide, onClose }) => (
  <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
    <div
      className="w-20 h-20 rounded-full flex items-center justify-center mb-4 text-3xl font-bold text-white"
      style={{ backgroundColor: guide.accentColor + '40' }}
    >
      {guide.name[0]}
    </div>
    <h3 className="text-xl font-bold text-white mb-2">
      Chat with {guide.name}
    </h3>
    <p className="text-slate-300 mb-6 max-w-sm">
      Sign in to get personalised advice from {guide.name}. It's free and helps {guide.name.toLowerCase()} remember your progress.
    </p>

    <a
      href="/signin"
      className="w-full max-w-sm bg-purple-600 text-white rounded-xl py-3 font-medium hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
    >
      Sign In to Chat
      <ArrowRight className="w-4 h-4" />
    </a>

    <button
      onClick={onClose}
      className="mt-4 text-slate-400 text-sm hover:text-white transition-colors"
    >
      Maybe later
    </button>
  </div>
);

/**
 * Main Guide Chat Modal
 */
const GuideChatModal = ({ isOpen, onClose }) => {
  const { currentView } = useApp();
  const { selectedGuide } = useGuide();
  const { user, isLoggedIn } = usePrivacyAuth();

  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Consent belongs to the shared coaching profile, not to this modal: the
  // Coaching page and the six guides are one coach, and the Hub refuses to
  // coach a profile without consent on it. 'unknown' is checked when the
  // modal opens and becomes 'granted' (chat) or 'needed' (the same gate the
  // Coaching page shows). The ref mirrors the state so the open/close effect
  // can read it without re-running on every change.
  const [consent, setConsent] = useState('unknown');
  const consentRef = useRef('unknown');
  const setConsentState = (value) => {
    consentRef.current = value;
    setConsent(value);
  };
  // A question the user typed before consent existed; sent for them once it does.
  const [pendingQuestion, setPendingQuestion] = useState(null);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const guide = selectedGuide;

  const suggestedQuestions = GuideSupportService.getSuggestedQuestions(
    guide.id,
    currentView
  );

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input and show welcome when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);

      if (messages.length === 0) {
        setMessages([{
          role: 'assistant',
          content: guide.welcomeMessage
        }]);
      }
    }
  }, [isOpen]);

  // Reset messages when guide changes
  useEffect(() => {
    if (guide && messages.length > 0) {
      setMessages([{
        role: 'assistant',
        content: guide.welcomeMessage
      }]);
    }
  }, [guide?.id]);

  // A different account is a different profile: forget what we knew.
  useEffect(() => {
    setConsentState('unknown');
  }, [user?.uid]);

  // On open, ask the profile whether consent has been granted - the question
  // the Coaching page has always asked before its first message.
  useEffect(() => {
    if (!isOpen || !isLoggedIn || consentRef.current !== 'unknown') return undefined;
    let cancelled = false;
    setConsentState('checking');
    GuideSupportService.getConsentStatus()
      .then((status) => {
        if (cancelled) return;
        // A profile we could read with no consent on it needs the gate. A read
        // that FAILED (offline, hub hiccup, token still settling) proves
        // nothing, so the chat opens and the Hub's own refusal, if it comes,
        // brings the gate up then - see handleSend.
        setConsentState(status.granted || status.error ? 'granted' : 'needed');
      })
      .catch(() => {
        if (!cancelled) setConsentState('granted');
      });
    return () => {
      cancelled = true;
      // Closed mid-check: leave it askable again next time, not stuck.
      if (consentRef.current === 'checking') setConsentState('unknown');
    };
  }, [isOpen, isLoggedIn, user?.uid]);

  const handleSend = useCallback(async (messageText = inputValue) => {
    if (!messageText.trim() || isLoading) return;

    const userMessage = { role: 'user', content: messageText.trim() };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setError(null);

    try {
      const conversationHistory = messages.map(m => ({
        role: m.role,
        content: m.content
      }));

      const result = await GuideSupportService.askGuide(
        messageText.trim(),
        guide.id,
        conversationHistory,
        // The page, plus the module and part on screen when the chat opened
        // over a module, so the guide knows what they are reading.
        { currentPage: currentView, ...moduleUserContext(getReadingModule()) }
      );

      if (result.error === 'consent-required') {
        // Setup, not failure: take the question back, show the gate, and send
        // it for them once consent is recorded.
        setMessages(prev => prev.filter(m => m !== userMessage));
        setPendingQuestion(messageText.trim());
        setConsentState('needed');
        return;
      }

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: result.response
      }]);
    } catch (err) {
      console.error('[GuideChatModal] Error:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [inputValue, isLoading, messages, guide?.id, currentView]);

  // Consent recorded: unlock the chat and send anything they had typed.
  const handleGrantConsent = useCallback(async (opts) => {
    const res = await GuideSupportService.grantConsent(opts);
    if (res?.success) {
      setConsentState('granted');
      setTimeout(() => inputRef.current?.focus(), 100);
    }
    return res;
  }, []);

  useEffect(() => {
    if (consent === 'granted' && pendingQuestion) {
      const question = pendingQuestion;
      setPendingQuestion(null);
      handleSend(question);
    }
  }, [consent, pendingQuestion, handleSend]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
      >
        {/* Backdrop */}
        <motion.div
          className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Modal */}
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="relative w-full max-w-lg bg-slate-800 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh] sm:max-h-[85vh]"
        >
          {/* Header */}
          <div
            className="px-6 py-4 flex items-center justify-between"
            style={{ backgroundColor: guide.accentColor }}
          >
            <div className="flex items-center gap-3">
              <motion.div
                className="w-12 h-12 rounded-full overflow-hidden border-2 border-white/30 flex items-center justify-center bg-white/20 text-white font-bold text-lg"
                animate={{ scale: [1, 1.03, 1] }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                {guide.name[0]}
              </motion.div>
              <div>
                <h2 className="text-lg font-bold text-white">
                  Chat with {guide.name}
                </h2>
                <p className="text-white/70 text-xs">
                  {guide.tagline}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-full transition-colors"
              aria-label="Close chat"
            >
              <X className="w-6 h-6 text-white" />
            </button>
          </div>

          {/* Sign in first; then consent, once; then chat. */}
          {!isLoggedIn ? (
            <SignInPrompt guide={guide} onClose={onClose} />
          ) : consent === 'needed' ? (
            <div className="flex-1 overflow-y-auto p-5 bg-slate-900" data-testid="guide-consent-gate">
              <ConsentGate
                theme="dark"
                coachName={guide.name}
                accentColor={guide.accentColor}
                onAccept={handleGrantConsent}
                onDecline={onClose}
              />
            </div>
          ) : consent !== 'granted' ? (
            <div className="flex-1 min-h-[300px] flex flex-col items-center justify-center gap-3 bg-slate-900 text-slate-400 text-sm">
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: guide.accentColor }} />
              Getting things ready…
            </div>
          ) : (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-[300px] bg-slate-900">
                {messages.map((msg, idx) => (
                  <MessageBubble
                    key={idx}
                    message={msg}
                    isUser={msg.role === 'user'}
                    guide={guide}
                  />
                ))}

                {isLoading && <TypingIndicator guide={guide} />}

                {error && (
                  <div className="flex items-center gap-2 text-red-400 text-sm px-2">
                    <AlertCircle className="w-4 h-4" />
                    {error}
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Suggested Questions */}
              {messages.length < 4 && (
                <div className="bg-slate-900">
                  <SuggestedQuestions
                    questions={suggestedQuestions}
                    onSelect={handleSend}
                    disabled={isLoading}
                    guide={guide}
                  />
                </div>
              )}

              {/* Input Area */}
              <div className="border-t border-slate-700 p-4 bg-slate-800">
                <div className="flex gap-2">
                  <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={`Ask ${guide.name} anything...`}
                    disabled={isLoading}
                    className="flex-1 bg-slate-700 border border-slate-600 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:border-transparent disabled:opacity-50"
                    style={{ '--tw-ring-color': guide.accentColor }}
                  />
                  <button
                    onClick={() => handleSend()}
                    disabled={!inputValue.trim() || isLoading}
                    className="rounded-xl px-4 py-3 text-white transition-colors disabled:opacity-50"
                    style={{ backgroundColor: guide.accentColor }}
                  >
                    {isLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                  </button>
                </div>

                {/* Mobile close button */}
                <button
                  onClick={onClose}
                  className="sm:hidden w-full mt-3 py-2.5 text-slate-400 text-sm font-medium hover:text-white transition-colors"
                >
                  Close Chat
                </button>
              </div>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default GuideChatModal;
